import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ──────────────────────────────────────────────────────────────────────────
// 2D pixel-art restorer that walks across the gallery between paintings
// and "fixes" them. Pure client visualisation — no backend calls.
//
// State machine (per cycle):
//   idle    → pick nearest card → walking
//   walking → CSS transition translates char to target. Legs animate, body bobs.
//   working → arms swing, legs static, sparkles. After WORK_MS → idle.
//
// External hooks:
//   onQueue([{id, image_url, title, status, progress}])  — emitted whenever
//     current target / queue / progress changes. status ∈
//     { 'walking' | 'working' | 'idle' | 'queued' }. progress ∈ [0,1] for
//     'working'; 0 otherwise.
// ──────────────────────────────────────────────────────────────────────────

const WALK_PX_PER_SEC = 180;
const WALK_MIN_MS = 600;
const WALK_MAX_MS = 5000;
const WORK_MS = 2400;
const INSPECT_MS = 700;
const IDLE_MS = 280;
const POLL_FOR_CARDS_MS = 200;
const POLL_TIMEOUT_MS = 2500;
const QUEUE_LEN = 3;
const PROGRESS_TICK_MS = 120;

// ── Sprite ─────────────────────────────────────────────────────────────────
const RestorerSprite = ({ phase, facingRight }) => {
  const walking = phase === 'walking';
  const working = phase === 'working';
  const chilling = phase === 'chilling';

  // When chilling we render a separate small "asleep" pixel-art figure
  // (lying on its side) plus floating Z's. The standing sprite becomes
  // disorienting to rotate cleanly with all the limb subgroups, so a
  // dedicated layout is simpler and reads better at small sizes.
  if (chilling) {
    return (
      <svg
        viewBox="0 0 48 36"
        width="60"
        height="46"
        shapeRendering="crispEdges"
        style={{
          transform: facingRight ? 'scaleX(-1)' : 'none',
          transformOrigin: 'center',
          filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))',
        }}
      >
        <defs>
          <style>{`
            @keyframes zFloat1 { 0% { opacity: 0; transform: translate(0,0); } 30% { opacity: 1; } 100% { opacity: 0; transform: translate(4px,-12px); } }
            @keyframes zFloat2 { 0% { opacity: 0; transform: translate(0,0); } 30% { opacity: 1; } 100% { opacity: 0; transform: translate(6px,-16px); } }
            @keyframes breathe { 0%,100% { transform: scaleY(1); } 50% { transform: scaleY(1.04); } }
            .breathe { animation: breathe 2.2s ease-in-out infinite; transform-origin: center; }
          `}</style>
        </defs>
        <g className="breathe">
          {/* Pillow (rolled coat) under head */}
          <rect x="2"  y="22" width="9"  height="4" fill="#2A2A3F" />
          <rect x="2"  y="21" width="9"  height="1" fill="#3B5C8A" />

          {/* Legs (lying horizontally) */}
          <rect x="32" y="20" width="12" height="3" fill="#1F2A3D" />
          <rect x="32" y="23" width="12" height="3" fill="#1F2A3D" />
          {/* Boots */}
          <rect x="42" y="20" width="4"  height="3" fill="#0C0C14" />
          <rect x="42" y="23" width="4"  height="3" fill="#0C0C14" />

          {/* Body / coat lying down */}
          <rect x="14" y="18" width="20" height="8" fill="#3B5C8A" />
          <rect x="20" y="20" width="2"  height="2" fill="#D4AF37" />

          {/* Arms folded across chest */}
          <rect x="18" y="20" width="10" height="2" fill="#2E4A6E" />

          {/* Head (face up) */}
          <rect x="9"  y="18" width="6"  height="6" fill="#E0BE9C" />
          {/* Closed eye line */}
          <rect x="11" y="20" width="2"  height="1" fill="#0C0C14" />
          {/* Mustache */}
          <rect x="11" y="22" width="3"  height="1" fill="#5A3A1A" />

          {/* Top hat tipped over face/back of head */}
          <rect x="3"  y="14" width="9"  height="5" fill="#1A1A28" />
          <rect x="2"  y="19" width="11" height="2" fill="#1A1A28" />
          <rect x="3"  y="17" width="9"  height="1" fill="#D4AF37" />
        </g>

        {/* Floating Z's */}
        <text x="14" y="14" fontSize="9" fontFamily="ui-monospace,monospace" fontWeight="bold" fill="#A1A1AA"
              style={{ animation: 'zFloat1 2s ease-out infinite' }}>z</text>
        <text x="20" y="9" fontSize="11" fontFamily="ui-monospace,monospace" fontWeight="bold" fill="#D4AF37"
              style={{ animation: 'zFloat2 2.4s ease-out infinite', animationDelay: '0.6s' }}>Z</text>
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 32 52"
      width="40"
      height="65"
      shapeRendering="crispEdges"
      style={{
        transform: facingRight ? 'scaleX(-1)' : 'none',
        transformOrigin: 'center',
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))',
      }}
    >
      <defs>
        <style>{`
          .walking .leg-l { animation: walkLegL 0.42s steps(1) infinite; transform-origin: 14px 42px; }
          .walking .leg-r { animation: walkLegR 0.42s steps(1) infinite; transform-origin: 18px 42px; }
          .working .arm-work { animation: armSwing 0.65s ease-in-out infinite; transform-origin: 11px 28px; }
          @keyframes walkLegL { 0%,49% { transform: rotate(-12deg); } 50%,100% { transform: rotate(14deg); } }
          @keyframes walkLegR { 0%,49% { transform: rotate(14deg); }  50%,100% { transform: rotate(-12deg); } }
          @keyframes armSwing { 0%,100% { transform: rotate(-26deg); } 50% { transform: rotate(20deg); } }
        `}</style>
      </defs>
      <g className={walking ? 'walking' : working ? 'working' : ''}>
        <rect x="22" y="14" width="2" height="36" fill="#7C5A2A" />
        <rect x="28" y="14" width="2" height="36" fill="#7C5A2A" />
        <rect x="22" y="20" width="8" height="2" fill="#A07840" />
        <rect x="22" y="28" width="8" height="2" fill="#A07840" />
        <rect x="22" y="36" width="8" height="2" fill="#A07840" />
        <rect x="22" y="44" width="8" height="2" fill="#A07840" />

        <rect x="9" y="6" width="10" height="6" fill="#1A1A28" />
        <rect x="7" y="12" width="14" height="2" fill="#1A1A28" />

        <rect x="11" y="14" width="8" height="6" fill="#E0BE9C" />

        <rect x="9" y="20" width="12" height="14" fill="#3B5C8A" />
        <rect x="13" y="22" width="4" height="2" fill="#D4AF37" />

        <g className="arm-work">
          <rect x="6" y="22" width="4" height="10" fill="#3B5C8A" />
          <rect x="3" y="27" width="4" height="2" fill="#A07840" />
          <rect x="0" y="26" width="3" height="4" fill="#F5D07A" />
        </g>

        <rect x="20" y="22" width="3" height="10" fill="#3B5C8A" />

        <rect className="leg-l" x="11" y="34" width="4" height="10" fill="#1F2A3D" />
        <rect className="leg-r" x="17" y="34" width="4" height="10" fill="#1F2A3D" />

        <rect x="10" y="44" width="6" height="3" fill="#0C0C14" />
        <rect x="16" y="44" width="6" height="3" fill="#0C0C14" />

        {working && (
          <>
            {/* Twinkles in place — keep the existing pair of glints */}
            <rect x="0" y="22" width="1" height="1" fill="#F5D07A" opacity="0.9">
              <animate attributeName="opacity" values="0.2;1;0.2" dur="0.7s" repeatCount="indefinite" />
            </rect>
            <rect x="2" y="20" width="1" height="1" fill="#F5D07A" opacity="0.6">
              <animate attributeName="opacity" values="1;0.2;1" dur="0.7s" repeatCount="indefinite" />
            </rect>

            {/* Spark particles flying out from the brush tip (~x=1, y=28).
                Each spark has its own staggered animation so the cluster
                feels chaotic rather than synchronised. SVG <animate>
                elements are CSS-free, ideal for this small budget. */}
            <g>
              <rect x="1" y="28" width="1" height="1" fill="#FFD24A">
                <animate attributeName="x"        values="1;-2;-5"       dur="0.55s" repeatCount="indefinite" />
                <animate attributeName="y"        values="28;25;22"      dur="0.55s" repeatCount="indefinite" />
                <animate attributeName="opacity"  values="1;0.7;0"       dur="0.55s" repeatCount="indefinite" />
                <animate attributeName="width"    values="1.2;0.8;0"     dur="0.55s" repeatCount="indefinite" />
                <animate attributeName="height"   values="1.2;0.8;0"     dur="0.55s" repeatCount="indefinite" />
              </rect>
              <rect x="1" y="28" width="1" height="1" fill="#FF9A2C">
                <animate attributeName="x"        values="1;-3;-6"       dur="0.7s" repeatCount="indefinite" begin="0.15s" />
                <animate attributeName="y"        values="28;30;33"      dur="0.7s" repeatCount="indefinite" begin="0.15s" />
                <animate attributeName="opacity"  values="1;0.6;0"       dur="0.7s" repeatCount="indefinite" begin="0.15s" />
                <animate attributeName="width"    values="1;0.6;0"       dur="0.7s" repeatCount="indefinite" begin="0.15s" />
                <animate attributeName="height"   values="1;0.6;0"       dur="0.7s" repeatCount="indefinite" begin="0.15s" />
              </rect>
              <rect x="1" y="28" width="1" height="1" fill="#FFE680">
                <animate attributeName="x"        values="1;-4;-7"       dur="0.6s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="y"        values="28;26;25"      dur="0.6s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="opacity"  values="1;0.8;0"       dur="0.6s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="width"    values="0.8;0.5;0"     dur="0.6s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="height"   values="0.8;0.5;0"     dur="0.6s" repeatCount="indefinite" begin="0.3s" />
              </rect>
              <rect x="1" y="28" width="1" height="1" fill="#FFB04A">
                <animate attributeName="x"        values="1;-1;-4"       dur="0.5s" repeatCount="indefinite" begin="0.45s" />
                <animate attributeName="y"        values="28;31;34"      dur="0.5s" repeatCount="indefinite" begin="0.45s" />
                <animate attributeName="opacity"  values="1;0.6;0"       dur="0.5s" repeatCount="indefinite" begin="0.45s" />
                <animate attributeName="width"    values="0.9;0.5;0"     dur="0.5s" repeatCount="indefinite" begin="0.45s" />
                <animate attributeName="height"   values="0.9;0.5;0"     dur="0.5s" repeatCount="indefinite" begin="0.45s" />
              </rect>
            </g>
          </>
        )}
      </g>
    </svg>
  );
};

// ── Walker ─────────────────────────────────────────────────────────────────
const RestorerWalker = ({ active, cards, onQueue, onEvent, onCompleteVisit }) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [facingRight, setFacingRight] = useState(false);
  const [transitionMs, setTransitionMs] = useState(0);
  // Pulse-mark above the head when inspecting a healthy card. Bumping
  // `tick` triggers a re-mount of the check-mark element so the CSS
  // animation replays cleanly each time.
  const [checkPulse, setCheckPulse] = useState(0);
  // Easter egg: tap-on-walker pops a "Busy" speech bubble. Bumping
  // the key re-mounts the bubble so the CSS animation replays. A
  // 10-second cooldown keeps the gag from becoming spammy.
  const [busyPulse, setBusyPulse] = useState(0);
  const lastBusyAtRef = useRef(0);

  const aliveRef = useRef(true);
  const phaseRef = useRef('idle');
  const posRef = useRef({ x: 0, y: 0 });
  const currentTargetRef = useRef(null);
  const queueRef = useRef([]); // upcoming nodes (excluding current)
  const recentRef = useRef([]); // last visited nodes
  const cardsByIdRef = useRef({});
  const onQueueRef = useRef(onQueue);
  const onEventRef = useRef(onEvent);
  const onCompleteVisitRef = useRef(onCompleteVisit);

  useEffect(() => { onQueueRef.current = onQueue; }, [onQueue]);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onCompleteVisitRef.current = onCompleteVisit; }, [onCompleteVisit]);

  useEffect(() => {
    const map = {};
    (cards || []).forEach((c) => { map[c.id] = c; });
    cardsByIdRef.current = map;
  }, [cards]);

  const setPhaseSafe = useCallback((p) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  const setPosSafe = useCallback((p) => {
    posRef.current = p;
    setPos(p);
  }, []);

  // Anchor for a card: left side, vertically centred. Sprite is 40×65.
  // Returns PAGE-relative coords (viewport rect + scroll offset) so the
  // walker — which is rendered into document.body via portal with
  // position:absolute — moves naturally with the page during scroll
  // without us having to listen for scroll events. We clamp the left
  // edge to keep the sprite from sticking past the viewport when a
  // leftmost-column card is the target (no horizontal scroll on mobile).
  const anchorFor = (node) => {
    const r = node.getBoundingClientRect();
    const minX = window.scrollX + 4;
    // Right side: when walker stands on a left-side anchor of a
    // right-column card, the sprite (~40px wide) can stick beyond the
    // viewport's right edge. Clamp so it stays fully visible — push
    // back ~6px from the rightmost edge.
    const SPRITE_W = 40;
    const maxX = window.scrollX + window.innerWidth - SPRITE_W - 6;
    let x = Math.round(r.left - 30 + window.scrollX);
    if (x < minX) x = minX;
    else if (x > maxX) x = maxX;
    return {
      x,
      y: Math.round(r.top + r.height / 2 - 32 + window.scrollY),
    };
  };

  const nodeCenter = (node) => {
    const r = node.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  const findCandidates = useCallback(() => {
    // Walker only walks among NON-EXHIBIT cards. Exhibit cards are
    // physically out of reach (hung in the gallery) — visiting them
    // would just waste time slapping a checkmark on something we
    // can't actually touch. They're filtered out here so the walker
    // never even visits them.
    const grid = document.querySelector('[data-testid="cards-grid"]');
    if (!grid) return [];
    const nodes = Array.from(grid.querySelectorAll('button[data-testid^="card-"]'));
    return nodes.filter((n) => {
      const r = n.getBoundingClientRect();
      return r.width > 30;
    });
  }, []);

  // Among the cards the walker actually visits, does this one need
  // repair? Live durability comes from /cards/me which already applies
  // decay, so we treat a sub-percent drop as "good as new" and use a
  // small tolerance (<99.5) as the work threshold. Without this, a
  // card that decayed 100 → 99.8 between polls would be diving into a
  // full work cycle every visit instead of getting a cheerful ✓.
  const needsRepair = useCallback((node) => {
    const id = (node?.getAttribute?.('data-testid') || '').replace(/^card-/, '');
    const card = cardsByIdRef.current[id];
    if (!card) return false;
    return (card.durability ?? 100) < 99.5;
  }, []);

  const nodeToId = (node) => {
    const tid = node?.getAttribute?.('data-testid') || '';
    return tid.replace(/^card-/, '');
  };

  // Compute nearest N candidates from `origin`, biased to fresh ones,
  // excluding `excludeNode` if provided.
  const nearestN = useCallback((origin, n, excludeNode = null) => {
    const all = findCandidates();
    const recent = new Set(recentRef.current);
    const filtered = all.filter((node) => node !== excludeNode);
    const fresh = filtered.filter((node) => !recent.has(node));
    const pool = fresh.length >= n ? fresh : filtered;
    return pool
      .map((node) => {
        const c = nodeCenter(node);
        return { node, dist: Math.hypot(c.x - origin.x, c.y - origin.y) };
      })
      .sort((a, b) => a.dist - b.dist)
      .slice(0, n)
      .map((x) => x.node);
  }, [findCandidates]);

  const emitQueue = useCallback((status, progress) => {
    const fn = onQueueRef.current;
    if (!fn) return;
    const build = (node, st, pr) => {
      const id = nodeToId(node);
      const card = cardsByIdRef.current[id] || {};
      return {
        id,
        image_url: card.image_url || '',
        title: card.title || '',
        image_focus_x: card.image_focus_x ?? 50,
        image_focus_y: card.image_focus_y ?? 50,
        status: st,
        progress: pr,
      };
    };
    const arr = [];
    if (currentTargetRef.current) {
      arr.push(build(currentTargetRef.current, status, progress));
    }
    for (const n of queueRef.current) arr.push(build(n, 'queued', 0));
    fn(arr);
  }, []);

  const waitForFirstCandidate = useCallback(async () => {
    const start = Date.now();
    while (aliveRef.current && Date.now() - start < POLL_TIMEOUT_MS) {
      const list = findCandidates();
      if (list.length) return list[0];
      await new Promise((r) => setTimeout(r, POLL_FOR_CARDS_MS));
    }
    return null;
  }, [findCandidates]);

  // Reposition on resize while standing still — page coords change only
  // on layout shifts (resize, sort, exhibit timer flip). Scroll is
  // handled implicitly by the browser since walker is page-absolute.
  useEffect(() => {
    if (!active || !visible) return;
    const stick = () => {
      const node = currentTargetRef.current;
      if (!node || !node.isConnected) return;
      if (phaseRef.current === 'walking') return;
      setTransitionMs(0);
      setPosSafe(anchorFor(node));
    };
    window.addEventListener('resize', stick);
    return () => {
      window.removeEventListener('resize', stick);
    };
  }, [active, visible, setPosSafe]);

  // Main loop — DOM-order inspector pattern.
  useEffect(() => {
    if (!active) {
      setVisible(false);
      setPhaseSafe('idle');
      currentTargetRef.current = null;
      queueRef.current = [];
      recentRef.current = [];
      if (onQueueRef.current) onQueueRef.current([]);
      return;
    }
    aliveRef.current = true;
    let cancelled = false;
    let progressTimer = null;
    let cycleIdx = 0;

    const clearProgressTimer = () => {
      if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
    };

    // Build the live queue display from current target + next 2 in DOM
    // order (regardless of repair status — we want the player to see
    // the inspection plan, not just work targets).
    const refreshQueueFromOrder = (orderedNodes, idx) => {
      const next1 = orderedNodes[(idx + 1) % orderedNodes.length] || null;
      const next2 = orderedNodes[(idx + 2) % orderedNodes.length] || null;
      queueRef.current = [next1, next2].filter(Boolean);
    };

    const run = async () => {
      const first = await waitForFirstCandidate();
      if (cancelled || !aliveRef.current) return;
      if (!first) return;

      const ordered = findCandidates();
      cycleIdx = 0;
      const startNode = ordered[0] || first;
      const spawn = anchorFor(startNode);
      setTransitionMs(0);
      // Spawn slightly to the left of the first card, but clamp so we
      // never pop in past the viewport edge (anchorFor already clamps,
      // applying the same floor here for safety).
      setPosSafe({ x: Math.max(window.scrollX + 4, spawn.x - 40), y: spawn.y });
      currentTargetRef.current = startNode;
      refreshQueueFromOrder(ordered, cycleIdx);
      setVisible(true);
      emitQueue('walking', 0);
      await new Promise((r) => setTimeout(r, 60));

      while (!cancelled && aliveRef.current) {
        // Re-read the ordered list every iteration — the gallery sort or
        // exhibit timers may have shifted things since last cycle.
        const cards = findCandidates();
        if (cards.length === 0) {
          await new Promise((r) => setTimeout(r, 600));
          continue;
        }

        // ── Sleep mode ────────────────────────────────────────────────
        // Only nap when (a) at least one full pass through the gallery
        // is complete AND (b) NOTHING is damaged. As long as a single
        // painting still needs work, the walker keeps rounding through
        // — backend repairs at ~1 dur/min so a single visit can't
        // really finish a heavily damaged painting, but visually the
        // restorer should look busy until the entire gallery is
        // healthy. The first-pass gate keeps him from bee-lining to
        // bed on first launch.
        const anyDamaged = cards.some((n) => needsRepair(n));
        const justFinishedPass = cycleIdx > 0 && cycleIdx % cards.length === 0;
        if (!anyDamaged && (justFinishedPass || phaseRef.current === 'chilling')) {
          // Compute bedtime anchor: horizontally centred under the
          // grid, ~16px below the last visible card.
          const grid = document.querySelector('[data-testid="cards-grid"]');
          let bedX = 24;
          let bedY = 0;
          if (grid) {
            const gr = grid.getBoundingClientRect();
            const lastCard = cards[cards.length - 1];
            const lastRect = lastCard ? lastCard.getBoundingClientRect() : null;
            bedX = Math.round(gr.left + gr.width / 2 - 30 + window.scrollX);
            bedY = Math.round(
              (lastRect ? lastRect.bottom : gr.bottom) - 16 + window.scrollY
            );
          }

          if (phaseRef.current !== 'chilling') {
            const dx = bedX - posRef.current.x;
            const dy = bedY - posRef.current.y;
            const dist = Math.hypot(dx, dy);
            const walkMs = Math.max(WALK_MIN_MS, Math.min(WALK_MAX_MS, (dist / WALK_PX_PER_SEC) * 1000));
            setFacingRight(dx >= 0);
            setTransitionMs(walkMs);
            setPosSafe({ x: bedX, y: bedY });
            setPhaseSafe('walking');
            emitQueue('walking', 0);
            await new Promise((r) => setTimeout(r, walkMs + 30));
            if (cancelled || !aliveRef.current) return;
          } else {
            setTransitionMs(0);
            setPosSafe({ x: bedX, y: bedY });
          }

          setPhaseSafe('chilling');
          emitQueue('chilling', 0);
          await new Promise((r) => setTimeout(r, 4000));
          cycleIdx = 0;
          continue;
        }

        // ── Pick the next card in DOM order (top-to-bottom). ──────
        // The walker rounds through every painting in sequence. If a
        // card is healthy he ticks ✓; if it still needs work he runs
        // another repair pass. No per-card cooldown — the backend
        // repairs slowly (1 dur/min) so partial work needs to
        // accumulate over many visits, and pretending a card is
        // "done" after one visit just makes him look idle while half
        // the gallery is still red.
        const target = cards[cycleIdx % cards.length];
        cycleIdx += 1;
        currentTargetRef.current = target;
        refreshQueueFromOrder(cards, (cycleIdx - 1) % cards.length);

        // ── Walk to current target ─────────────────────────────────
        // Speed is constant px/sec with generous min/max so short
        // hops still register as a deliberate step and long traversals
        // don't visually teleport.
        const a = anchorFor(target);
        const dx = a.x - posRef.current.x;
        const dy = a.y - posRef.current.y;
        const dist = Math.hypot(dx, dy);
        const walkMs = Math.max(WALK_MIN_MS, Math.min(WALK_MAX_MS, (dist / WALK_PX_PER_SEC) * 1000));
        setFacingRight(dx >= 0);
        setTransitionMs(walkMs);
        setPosSafe(a);
        setPhaseSafe('walking');
        emitQueue('walking', 0);

        await new Promise((r) => setTimeout(r, walkMs + 30));
        if (cancelled || !aliveRef.current) return;

        const willWork = needsRepair(target);

        if (willWork) {
          // Single continuous progress bar per painting visit. The
          // visual visit always finishes (we never leave a half-fixed
          // bar on screen) but the *real* repair is committed to the
          // backend at the end of the visit via `onCompleteVisit`.
          // Backend deducts coin cost and snaps durability — so the
          // card detail bar updates the moment the walker steps away.
          const startCard = cardsByIdRef.current[nodeToId(target)] || {};
          const startDur = Math.max(0, Math.min(100, startCard.durability ?? 100));
          const gap = Math.max(0.5, 100 - startDur);
          const repairMs = Math.max(2400, Math.min(24000, gap * 800));
          const visitStart = Date.now();

          setPhaseSafe('working');
          emitQueue('working', 0);
          progressTimer = setInterval(() => {
            if (cancelled || !aliveRef.current || phaseRef.current !== 'working') return;
            const liveCard = cardsByIdRef.current[nodeToId(target)] || {};
            const liveDur = Math.max(0, Math.min(100, liveCard.durability ?? startDur));
            const realProgress = gap > 0 ? (liveDur - startDur) / gap : 1;
            const timeProgress = (Date.now() - visitStart) / repairMs;
            const p = Math.max(0, Math.min(1, Math.max(realProgress, timeProgress)));
            emitQueue('working', p);
          }, PROGRESS_TICK_MS);

          await new Promise((r) => setTimeout(r, repairMs));
          clearProgressTimer();
          if (cancelled || !aliveRef.current) return;
          emitQueue('working', 1);

          // Commit the repair to the backend. The endpoint returns the
          // new durability (100 unless coins ran out) and toggles the
          // `blocked` flag for the TeamPanel. We don't block the loop
          // forever if the network is slow — 5s timeout.
          if (onCompleteVisitRef.current) {
            const finishedId = nodeToId(target);
            try {
              await Promise.race([
                onCompleteVisitRef.current(finishedId),
                new Promise((res) => setTimeout(res, 5000)),
              ]);
            } catch (e) {
              // Soft-fail — the offline tick (apply_restorer_tick) will
              // still trickle repair in if this call ever stalls.
              // eslint-disable-next-line no-console
              console.warn('Restorer complete-visit failed:', e);
            }
          }

          if (onEventRef.current) {
            const finishedId = nodeToId(target);
            const finishedCard = cardsByIdRef.current[finishedId] || {};
            onEventRef.current({
              id: `${finishedId}-${Date.now()}`,
              worker: 'restorer',
              action: 'fixed',
              card_id: finishedId,
              title: finishedCard.title || '',
              image_url: finishedCard.image_url || '',
              at: new Date().toISOString(),
            });
          }
        } else {
          // Healthy card: pop the inspect ✓ instead of a useless
          // work cycle.
          setPhaseSafe('idle');
          emitQueue('idle', 0);
          setCheckPulse((n) => n + 1);
          await new Promise((r) => setTimeout(r, INSPECT_MS));
          if (cancelled || !aliveRef.current) return;
        }

        // Brief idle before stepping to the next card.
        setPhaseSafe('idle');
        emitQueue('idle', 0);
        await new Promise((r) => setTimeout(r, IDLE_MS));
      }
    };

    run();
    return () => {
      cancelled = true;
      aliveRef.current = false;
      clearProgressTimer();
    };
  }, [active, needsRepair, waitForFirstCandidate, setPhaseSafe, setPosSafe, emitQueue, findCandidates]);

  if (!active || !visible) return null;

  const bobClass = phase === 'walking' ? 'restorer-bob' : '';

  // Mount via portal to body so position:absolute coords are PAGE-relative
  // (no positioned ancestors interfere). Walker scrolls naturally with
  // the page during pan/scroll — no per-frame JS needed.
  const node = (
    <>
      <style>{`
        @keyframes restorer-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
        .restorer-bob { animation: restorer-bob 0.42s steps(2) infinite; }
        @keyframes restorer-check { 0% { transform: translate(-50%, 4px) scale(0.7); opacity: 0; } 25% { transform: translate(-50%, -2px) scale(1.15); opacity: 1; } 75% { transform: translate(-50%, -8px) scale(1); opacity: 1; } 100% { transform: translate(-50%, -16px) scale(0.85); opacity: 0; } }
        @keyframes restorer-busy {
          0%   { transform: translate(-50%, 6px) scale(0.6); opacity: 0; }
          18%  { transform: translate(-50%, -2px) scale(1.08); opacity: 1; }
          28%  { transform: translate(-50%, -4px) scale(1); opacity: 1; }
          85%  { transform: translate(-50%, -4px) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -10px) scale(0.95); opacity: 0; }
        }
      `}</style>
      <div
        data-testid="restorer-walker"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`,
          transition: transitionMs > 0 ? `transform ${transitionMs}ms linear` : 'none',
          pointerEvents: 'none',
          zIndex: 30,
          willChange: 'transform',
        }}
      >
        <div className={bobClass} style={{ position: 'relative' }}>
          {/* Busy speech bubble — easter egg triggered by tapping the
              walker. 10s cooldown so it doesn't become spammy. */}
          {busyPulse > 0 && (
            <div
              key={`busy-${busyPulse}`}
              data-testid="restorer-busy-bubble"
              style={{
                position: 'absolute',
                left: '50%',
                bottom: 'calc(100% + 4px)',
                pointerEvents: 'none',
                animation: 'restorer-busy 2s ease-out forwards',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  padding: '4px 10px',
                  background: '#0E0E18',
                  border: '1px solid rgba(212,175,55,0.55)',
                  borderRadius: 10,
                  color: '#F5E0A0',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.55), 0 0 14px rgba(212,175,55,0.18)',
                }}
              >
                Busy
                {/* Tail */}
                <span
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '100%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '5px solid #0E0E18',
                    filter: 'drop-shadow(0 1px 0 rgba(212,175,55,0.45))',
                  }}
                />
              </div>
            </div>
          )}

          {/* Green ✓ pulse above the hat — replays each `checkPulse` bump
              by changing the React key. Pure CSS, ~700ms duration. */}
          {checkPulse > 0 && (
            <div
              key={`check-${checkPulse}`}
              style={{
                position: 'absolute',
                left: '50%',
                top: -10,
                width: 16,
                height: 16,
                pointerEvents: 'none',
                animation: 'restorer-check 0.7s ease-out forwards',
              }}
            >
              <svg viewBox="0 0 16 16" width="16" height="16">
                <circle cx="8" cy="8" r="7" fill="#10B981" stroke="#0C0C14" strokeWidth="1" />
                <path d="M4 8.5 L7 11.5 L12 5.5" stroke="#0C0C14" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          {/* Hit-target wrapper around the sprite so we can capture
              taps for the easter egg without disabling pointer-events
              on the outer walker container (which would steal scroll
              / tap behaviour from the underlying cards). */}
          <div
            data-testid="restorer-hit-area"
            onClick={(e) => {
              e.stopPropagation();
              const now = Date.now();
              if (now - lastBusyAtRef.current < 10000) return;
              lastBusyAtRef.current = now;
              setBusyPulse((n) => n + 1);
              try {
                window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
              } catch { /* ignore */ }
            }}
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
          >
            <RestorerSprite phase={phase} facingRight={facingRight} />
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(node, document.body);
};

export default RestorerWalker;
